import { Play, Pause } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";

type AudioPlayerProps = {
  audioUrl: string;
  isCustomer?: boolean;
  /**
   * Play-button colour. "blue" is the original, kept for the composer's recording
   * preview. "neutral" is for message bubbles that already carry their own tint
   * (green for the AI, palomma purple for an advisor) — a third hue on top of
   * those reads as decoration, which DESIGN.md reserves colour against.
   */
  accent?: "blue" | "neutral";
};

const AudioPlayer = ({ audioUrl, isCustomer = false, accent = "blue" }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (value: number[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = value[0];
    setCurrentTime(value[0]);
  };

  const formatTime = (time: number) => {
    if (!isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="w-full">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      <div className="flex items-center gap-2">
        <button
          aria-label={isPlaying ? "Pause" : "Play"}
          onClick={togglePlay}
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-md",
            accent === "neutral"
              ? "bg-white text-neutral-900 shadow-sm hover:bg-neutral-100"
              : isCustomer
                ? "bg-white text-blue-500 hover:bg-gray-100"
                : "bg-blue-500 text-white hover:bg-blue-600"
          )}
        >
          {isPlaying ? <Pause className="size-4" /> : <Play className="size-4" />}
        </button>
        <Slider
          aria-label="audio-progress"
          value={[currentTime]}
          min={0}
          max={duration || 0}
          step={0.1}
          onValueChange={handleSliderChange}
          className="flex-1"
        />
        <span
          className={cn(
            "min-w-[45px] text-xs",
            isCustomer ? "opacity-90" : "opacity-70"
          )}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
    </div>
  );
};

export default AudioPlayer;
